import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEuro, formatEuroCompact } from "@/lib/utils";
import { DateRange } from "./DateFilter";
import { useWorkspace } from "@/hooks/useWorkspace";

interface SalesByCategoryProps {
  dateRange: DateRange;
}

interface CategoryData {
  category: string;
  total: number;
}

export function SalesByCategory({ dateRange }: SalesByCategoryProps) {
  const [data, setData] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [dateRange, workspaceId]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: orderItems } = await supabase
        .from("order_items")
        .select(`
          total,
          product_id,
          products(category),
          order_id,
          orders!inner(order_date, status, workspace_id)
        `)
        .eq("orders.workspace_id", workspaceId)
        .gte("orders.order_date", dateRange.from.toISOString().split("T")[0])
        .lte("orders.order_date", dateRange.to.toISOString().split("T")[0])
        .neq("orders.order_type", "purchase")
        .in("orders.status", ["completed", "delivered", "shipped"]);

      const categoryTotals: Record<string, number> = {};

      orderItems?.forEach((item) => {
        const category = (item.products as any)?.category || "Sin categoría";
        categoryTotals[category] = (categoryTotals[category] || 0) + (Number(item.total) || 0);
      });

      const chartData = Object.entries(categoryTotals)
        .map(([category, total]) => ({ category, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8);

      setData(chartData);
    } catch (error) {
      console.error("Error fetching sales by category:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">Ventas por Categoría</h3>
          <p className="text-sm text-muted-foreground">Ingresos por tipo de producto</p>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const hasData = data.length > 0;

  return (
    <div className="chart-container animate-slide-in-up">
      <div className="mb-4">
        <h3 className="font-display text-lg font-semibold text-foreground">Ventas por Categoría</h3>
        <p className="text-sm text-muted-foreground">Ingresos por tipo de producto</p>
      </div>
      {!hasData ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">No hay ventas en este período</div>
      ) : (
        <div className="h-64 w-full overflow-x-auto">
          <ResponsiveContainer width="100%" height="100%" minWidth={300}>
            <BarChart data={data} layout="vertical" margin={{ left: 0, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 47%, 22%)" />
              <XAxis type="number" stroke="hsl(215, 20%, 65%)" tick={{ fill: "hsl(215, 20%, 65%)", fontSize: 10 }} tickFormatter={(value) => formatEuroCompact(value)} />
              <YAxis type="category" dataKey="category" stroke="hsl(215, 20%, 65%)" tick={{ fill: "hsl(215, 20%, 65%)", fontSize: 9 }} width={80} />
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number) => [formatEuro(value), "Ventas"]} />
              <Bar dataKey="total" fill="hsl(213, 94%, 55%)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
